import { Copy, Pipette } from "lucide-react";
import { type PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { oklchToRgb255 } from "@/color/fast";
import { formatColor, fromLch, type Hex, lchInGamut, parseColor, toLch } from "@/color/oklch";
import { useUi } from "@/store/ui";
import { Button } from "@/ui/kit/button";
import { Tip } from "@/ui/kit/tooltip";
import { ColorField } from "./color-field";
import { GradientSlider } from "./slider";

const MAX_C = 0.37;
const W = 240;
const H = 132;

function stops(fn: (t: number) => Hex, n = 12): string {
  return `linear-gradient(to right, ${Array.from({ length: n + 1 }, (_, i) => fn(i / n)).join(", ")})`;
}

function Plane({
  l,
  c,
  h,
  onPick,
}: {
  l: number;
  c: number;
  h: number;
  onPick: (l: number, c: number) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  // Redrawing 30k pixels is the most expensive thing on screen: only do it per whole degree.
  const hue = Math.round(h);
  useEffect(() => {
    const ctx = canvas.current?.getContext("2d");
    if (!ctx) return;
    const img = ctx.createImageData(W, H);
    for (let y = 0; y < H; y++) {
      const ll = 1 - y / (H - 1);
      for (let x = 0; x < W; x++) {
        const cc = (x / (W - 1)) * MAX_C;
        const [r, g, b, ok] = oklchToRgb255(ll, cc, hue);
        const i = (y * W + x) * 4;
        img.data[i] = r;
        img.data[i + 1] = g;
        img.data[i + 2] = b;
        img.data[i + 3] = ok ? 255 : 46;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [hue]);

  const pick = (e: PointerEvent) => {
    const box = canvas.current?.getBoundingClientRect();
    if (!box) return;
    const x = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    const y = Math.min(1, Math.max(0, (e.clientY - box.top) / box.height));
    onPick(1 - y, x * MAX_C);
  };

  return (
    <div className="checker relative overflow-hidden rounded-lg ring-1 ring-line">
      <canvas
        ref={canvas}
        width={W}
        height={H}
        className="block h-[132px] w-full cursor-crosshair touch-none"
        aria-label="Lightness and chroma plane"
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          pick(e);
        }}
        onPointerMove={(e) => e.buttons === 1 && pick(e)}
      />
      <span
        className="pointer-events-none absolute size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,.6)]"
        style={{ left: `${(c / MAX_C) * 100}%`, top: `${(1 - l) * 100}%` }}
      />
      <span className="pointer-events-none absolute right-1.5 bottom-1 text-[9px] text-white/60 mix-blend-difference">
        chroma →
      </span>
    </div>
  );
}

export function OklchPicker({ value, onChange }: { value: Hex; onChange: (hex: Hex) => void }) {
  const lch = toLch(value);
  // The hue is kept as state: hex round trips make it jitter, and a gray has none, so dragging
  // chroma to 0 and back would lose it. It follows `value` whenever the color changes from
  // outside this picker (another swatch, undo, a generator); a gray from outside gets hue 0.
  // Callers key the picker by what it edits, so a swatch never inherits another's hue.
  const [hue, setHue] = useState(lch.h);
  const emitted = useRef<Hex | null>(null);
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = null;
    setHue(toLch(value).h);
  }, [value]);
  const { l, c } = lch;
  const h = hue;
  const outOfGamut = !lchInGamut({ l, c, h });

  const set = (next: Partial<{ l: number; c: number; h: number }>) => {
    if (next.h !== undefined) setHue(next.h);
    const hex = fromLch({ l, c, h, ...next });
    emitted.current = hex;
    onChange(hex);
  };

  const gradients = useMemo(
    () => ({
      l: stops((t) => fromLch({ l: t, c, h })),
      c: stops((t) => fromLch({ l, c: t * MAX_C, h })),
      h: stops((t) => fromLch({ l, c: Math.max(c, 0.08), h: t * 360 }), 18),
    }),
    [l, c, h],
  );

  const format = useUi((s) => s.colorFormat);
  const setFormat = useUi((s) => s.setColorFormat);
  const eyedropper = typeof window !== "undefined" && "EyeDropper" in window;

  return (
    <div className="space-y-2.5">
      <Plane l={l} c={c} h={h} onPick={(nl, nc) => set({ l: nl, c: nc })} />
      <GradientSlider
        label="L"
        value={l}
        min={0}
        max={1}
        step={0.005}
        onChange={(nl) => set({ l: nl })}
        gradient={gradients.l}
        format={(n) => `${(n * 100).toFixed(1)}%`}
      />
      <GradientSlider
        label="C"
        value={c}
        min={0}
        max={MAX_C}
        step={0.002}
        onChange={(nc) => set({ c: nc })}
        gradient={gradients.c}
        format={(n) => n.toFixed(3)}
      />
      <GradientSlider
        label="H"
        value={h}
        min={0}
        max={360}
        step={0.5}
        onChange={(nh) => set({ h: nh })}
        gradient={gradients.h}
        format={(n) => `${n.toFixed(0)}°`}
      />
      <div className="flex gap-1.5">
        <ColorField value={value} onChange={onChange} />
        {eyedropper && (
          <Tip label="Pick a color from the screen">
            <Button
              size="icon"
              variant="outline"
              aria-label="Eyedropper"
              onClick={async () => {
                try {
                  // biome-ignore lint/suspicious/noExplicitAny: EyeDropper is not in lib.dom yet
                  const res = await new (window as any).EyeDropper().open();
                  const hex = parseColor(res.sRGBHex);
                  if (hex) onChange(hex);
                } catch {
                  /* cancelled */
                }
              }}
            >
              <Pipette />
            </Button>
          </Tip>
        )}
      </div>
      <div className="flex items-center gap-1">
        {(["hex", "rgb", "hsl", "oklch"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFormat(f)}
            className={`rounded px-1.5 py-0.5 text-[11px] ${format === f ? "bg-raised text-fg" : "text-faint hover:text-muted"}`}
          >
            {f}
          </button>
        ))}
        <button
          type="button"
          className="ml-auto flex min-w-0 items-center gap-1 truncate rounded px-1.5 py-0.5 font-mono text-[11px] text-muted hover:bg-raised hover:text-fg"
          onClick={() => {
            navigator.clipboard.writeText(formatColor(value, format));
            toast.success(`Copied ${formatColor(value, format)}`);
          }}
          title="Copy"
        >
          <span className="truncate">{formatColor(value, format)}</span>
          <Copy className="size-3 shrink-0" />
        </button>
      </div>
      {outOfGamut && (
        <p className="text-[11px] text-warn">
          Outside sRGB: mapped to the nearest displayable color.
        </p>
      )}
    </div>
  );
}
